import React, { useState, useRef, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import { WrenchIcon, LightBulbIcon, DocumentTextIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import { useTranslation } from 'react-i18next';
import DotenvImportButton from './DotenvImportButton';
import { fusionnerVariables, DotenvEntry } from '../utils/parseDotenv';

let _envEditorIdCounter = 0;
const nextId = () => `evar-${++_envEditorIdCounter}`;

export interface EnvironmentVariable {
  name: string;
  value: string;
}

interface EnvironmentVariablesEditorProps {
  variables: EnvironmentVariable[];
  onChange: (variables: EnvironmentVariable[]) => void;
}

const EnvironmentVariablesEditor: React.FC<EnvironmentVariablesEditorProps> = ({
  variables,
  onChange
}) => {
  const { t } = (useTranslation as any)('deployments');
  const [isExpanded, setIsExpanded] = useState(false);
  const idsRef = useRef<string[]>([]);

  // Keep stable IDs in sync with variables length
  while (idsRef.current.length < variables.length) {
    idsRef.current.push(nextId());
  }
  idsRef.current.length = variables.length;

  const addVariable = () => {
    idsRef.current.push(nextId());
    const newVariables = [...variables, { name: '', value: '' }];
    onChange(newVariables);
    setIsExpanded(true);
  };

  const updateVariable = (index: number, field: 'name' | 'value', value: string) => {
    const newVariables = [...variables];
    newVariables[index][field] = value;
    onChange(newVariables);
  };

  const removeVariable = (index: number) => {
    idsRef.current.splice(index, 1);
    const newVariables = variables.filter((_, i) => i !== index);
    onChange(newVariables);
    toast.success('Variable supprimée');
  };

  const duplicateVariable = (index: number) => {
    const variableToDuplicate = variables[index];
    idsRef.current.push(nextId());
    const newVariables = [...variables, {
      name: `${variableToDuplicate.name}_copy`,
      value: variableToDuplicate.value
    }];
    onChange(newVariables);
    toast.success('Variable dupliquée');
  };

  const importerDotenv = (entries: DotenvEntry[]) => {
    const fusion = fusionnerVariables(variables, entries);
    // Les identifiants stables sont indexes : apres une fusion qui retire les
    // lignes vides, les index ont bouge. On les regenere en bloc plutot que de
    // tenter un recalage -- au prix d'un remontage des lignes, invisible ici
    // puisque la liste vient d'etre remplacee.
    idsRef.current = [];
    onChange(fusion.variables);
    setIsExpanded(true);
  };

  const clearAllVariables = () => {
    if (variables.length === 0) return;

    if (window.confirm('Êtes-vous sûr de vouloir supprimer toutes les variables d\'environnement ?')) {
      onChange([]);
      toast.success('Toutes les variables ont été supprimées');
    }
  };

  return (
    <div className="bg-surface-2 rounded-lg border border-border-1 p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-br from-accent-500 to-accent-600 rounded-lg flex items-center justify-center">
            <WrenchIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-text-primary flex items-center">
              Variables d'Environnement
              {variables.length > 0 && (
                <span className="ml-2 inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-500/12 text-purple-400">
                  {variables.length} {variables.length === 1 ? 'variable' : 'variables'}
                </span>
              )}
            </h3>
            <p className="text-sm text-text-tertiary">
              Variables injectées lors du build et du runtime
            </p>
          </div>
        </div>

        {/* Toggle Button */}
        <button
          type="button"
          onClick={() => setIsExpanded(!isExpanded)}
          aria-expanded={isExpanded}
          aria-label={isExpanded ? 'Masquer les variables' : 'Afficher les variables'}
          className="text-text-quaternary hover:text-text-secondary transition-colors focus:outline-none focus:ring-2 focus:ring-purple-500 focus:ring-offset-2 rounded"
        >
          <svg
            className={`w-6 h-6 transform transition-transform ${isExpanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </button>
      </div>

      {/* Info Box */}
      {!isExpanded && variables.length === 0 && (
        <div className="p-4 bg-purple-500/12 rounded-md">
          <p className="text-sm text-purple-400">
            <LightBulbIcon className="w-4 h-4 inline mr-1" /><strong>Conseil:</strong> Ajoutez des variables d'environnement si votre application a besoin de configuration (API keys, URLs, etc.)
          </p>
        </div>
      )}

      {/* Expanded Content */}
      {isExpanded && (
        <div className="space-y-4">
          {/* Variables List */}
          {variables.length > 0 ? (
            <div className="space-y-3">
              {variables.map((variable, index) => (
                <div
                  key={idsRef.current[index]}
                  className="flex items-start space-x-3 p-3 bg-surface-3 rounded-lg border border-border-1"
                >
                  {/* Variable Name */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Nom
                    </label>
                    <input
                      type="text"
                      value={variable.name}
                      onChange={(e) => updateVariable(index, 'name', e.target.value)}
                      placeholder="API_KEY"
                      className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* Variable Value */}
                  <div className="flex-1">
                    <label className="block text-xs font-medium text-text-secondary mb-1">
                      Valeur
                    </label>
                    <input
                      type="text"
                      value={variable.value}
                      onChange={(e) => updateVariable(index, 'value', e.target.value)}
                      placeholder="your-secret-value"
                      className="w-full px-3 py-2 border border-border-1 bg-surface-3 text-text-primary rounded-md focus:outline-none focus:ring-2 focus:ring-purple-500 focus:border-transparent text-sm font-mono"
                    />
                  </div>

                  {/* Actions */}
                  <div className="flex items-end space-x-1 pt-6">
                    <button
                      type="button"
                      onClick={() => duplicateVariable(index)}
                      aria-label={`Dupliquer la variable ${variable.name || 'sans nom'}`}
                      className="p-2 text-blue-400 hover:bg-blue-500/12 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                    <button
                      type="button"
                      onClick={() => removeVariable(index)}
                      aria-label={`Supprimer la variable ${variable.name || 'sans nom'}`}
                      className="p-2 text-red-400 hover:bg-red-500/12 rounded transition-colors focus:outline-none focus:ring-2 focus:ring-red-500"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 border-2 border-dashed border-border-1 rounded-lg">
              <DocumentTextIcon className="w-10 h-10 text-text-quaternary mb-3 mx-auto" />
              <h4 className="text-lg font-medium text-text-primary mb-2">
                Aucune variable d'environnement
              </h4>
              <p className="text-sm text-text-tertiary mb-4">
                Ajoutez des variables pour configurer votre application
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-between pt-4 border-t border-border-1">
            <div className="flex items-center gap-3">
              <button
                type="button"
                onClick={addVariable}
                className="inline-flex items-center px-4 py-2 bg-purple-600 text-white rounded-md hover:bg-purple-700 transition-colors text-sm font-medium"
              >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Ajouter une variable
              </button>
              <DotenvImportButton onImport={importerDotenv} contexte="config" />
            </div>

            {variables.length > 0 && (
              <button
                type="button"
                onClick={clearAllVariables}
                className="text-sm text-red-400 hover:text-red-300 font-medium"
              >
                Tout supprimer
              </button>
            )}
          </div>

          {/* Info & Security Note */}
          <div className="mt-4 p-4 bg-yellow-500/12 rounded-lg border border-yellow-500/30">
            <div className="flex items-start">
              <ExclamationTriangleIcon className="w-6 h-6 text-yellow-500 mr-3 flex-shrink-0" />
              <div className="text-sm text-yellow-400">
                <p className="font-medium mb-1">Note de sécurité</p>
                <ul className="list-disc list-inside space-y-1 text-xs">
                  <li>Évitez de stocker des secrets sensibles directement ici</li>
                  <li>Les variables sont visibles dans les logs de build</li>
                  <li>Utilisez un gestionnaire de secrets pour les données critiques</li>
                  <li>Format: <code className="bg-yellow-500/12 px-1 rounded">VARIABLE_NAME=value</code></li>
                </ul>
              </div>
            </div>
          </div>

          {/* Common Variables Examples */}
          <div className="mt-4 p-4 bg-blue-500/12 rounded-lg border border-blue-500/30">
            <div className="flex items-start">
              <LightBulbIcon className="w-6 h-6 text-blue-500 mr-3 flex-shrink-0" />
              <div className="text-sm text-blue-400">
                <p className="font-medium mb-1">Exemples courants</p>
                <div className="grid grid-cols-2 gap-2 text-xs mt-2">
                  <div>
                    <code className="bg-blue-500/12 px-2 py-1 rounded block">NODE_ENV=production</code>
                  </div>
                  <div>
                    <code className="bg-accent-500/12 px-2 py-1 rounded block">API_URL=https://api.example.com</code>
                  </div>
                  <div>
                    <code className="bg-accent-500/12 px-2 py-1 rounded block">PORT=8080</code>
                  </div>
                  <div>
                    <code className="bg-accent-500/12 px-2 py-1 rounded block">DEBUG=false</code>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EnvironmentVariablesEditor;
